import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DashLayout from '../../components/DashLayout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' }
];

const STATUS_COLORS = {
  approved: { bg: 'rgba(0,213,91,0.1)', color: '#00d55b', label: '✓ Approved' },
  pending:  { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', label: '⏳ Pending' },
  pending_verification: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', label: '⏳ Under Review' },
  rejected: { bg: 'rgba(255,68,68,0.1)', color: '#ff4444', label: '✗ Rejected' }
};

export default function Billing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleRenew = () => {
    // Pre-fill sessionStorage with current plan info so PaymentPage shows correct currency
    const p = profile;
    sessionStorage.setItem('pay_email', p?.email || user?.email || '');
    sessionStorage.setItem('pay_company', p?.company_name || '');
    sessionStorage.setItem('pay_plan', p?.plan_key || 'pack_one');
    sessionStorage.setItem('pay_currency', p?.currency || 'inr');
    sessionStorage.setItem('pay_cycle', p?.billing_cycle || 'monthly');
    sessionStorage.setItem('pay_business_id', String(p?.id || user?.id || ''));
    sessionStorage.setItem('pay_address', p?.address || '');
    sessionStorage.setItem('pay_gstin', p?.gstin || '');
    navigate('/payment');
  };

  useEffect(() => {
    Promise.all([
      axios.get('/api/business/profile').catch(() => ({ data: { data: null } })),
      axios.get('/api/payment/my-payments').catch(() => ({ data: { data: [] } }))
    ]).then(([profileRes, paymentsRes]) => {
      setProfile(profileRes.data.data);
      setPayments(paymentsRes.data.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const openInvoice = (txnId) => {
    window.open(`https://api.mschat.msdiscoveries.com/api/payment/invoice/${txnId}`, '_blank');
  };

  const printInvoice = (txnId) => {
    window.open(`https://api.mschat.msdiscoveries.com/api/payment/invoice/${txnId}?print=1`, '_blank');
  };

  const curr = profile?.currency === 'usd' ? '$' : '₹';
  const daysLeft = profile?.plan_expires_at
    ? Math.ceil((new Date(profile.plan_expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 24, maxWidth: 900 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Billing & Invoices</h1>
        <p style={{ color: '#555', fontSize: 13, marginBottom: 28 }}>Manage your subscription and download invoices</p>

        {loading ? (
          <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>Loading...</div>
        ) : (
          <>
            {/* Current Plan Card */}
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 24, marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Current Plan</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: '#00d55b', marginBottom: 4 }}>
                    {profile?.plan_name || profile?.plan_key?.replace(/_/g, ' ') || 'No Plan'}
                  </div>
                  <div style={{ fontSize: 13, color: '#888' }}>
                    {profile?.billing_cycle === 'yearly' ? 'Yearly billing' : profile?.billing_cycle === 'quarterly' ? 'Quarterly billing' : 'Monthly billing'} · {profile?.currency?.toUpperCase()}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    display: 'inline-block', padding: '6px 16px', borderRadius: 20,
                    background: profile?.account_status === 'active' ? 'rgba(0,213,91,0.1)' : 'rgba(255,68,68,0.1)',
                    color: profile?.account_status === 'active' ? '#00d55b' : '#ff4444',
                    fontSize: 13, fontWeight: 600, marginBottom: 8
                  }}>
                    {profile?.account_status === 'active' ? '✓ Active' : profile?.account_status?.replace(/_/g, ' ')}
                  </div>
                  {profile?.plan_expires_at && (
                    <div style={{ fontSize: 12, color: daysLeft !== null && daysLeft <= 7 ? '#ff4444' : '#555', marginTop: 3 }}>
                      Expires: {new Date(profile.plan_expires_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                    </div>
                  )}
                </div>
              </div>

              {/* Plan features */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginTop: 20, paddingTop: 20, borderTop: '1px solid #1e1e1e' }}>
                {[
                  ['👥', 'Max Agents', profile?.max_agents],
                  ['💬', 'Chats/month', profile?.max_chats_per_month === 999999 ? 'Unlimited' : profile?.max_chats_per_month?.toLocaleString()],
                  ['🤖', 'AI Chatbot', profile?.has_ai ? 'Included' : 'Not included'],
                  ['🎨', 'Custom Branding', profile?.has_custom_branding ? 'Included' : 'Not included'],
                  ['📈', 'Analytics', profile?.has_analytics ? 'Included' : 'Not included'],
                ].map(([icon, label, val]) => (
                  <div key={label} style={{ background: '#0d0d0d', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>{icon} {label}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: val === 'Not included' ? '#333' : '#fff' }}>{val || '—'}</div>
                  </div>
                ))}
              </div>

              {/* Renew notice */}
              {daysLeft !== null && daysLeft <= 14 && daysLeft > 0 && (
                <div style={{ marginTop: 16, padding: '14px 16px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ fontSize: 13, color: '#f59e0b' }}>
                    ⚠️ Your plan expires in <strong>{daysLeft} days</strong>. Renew now to avoid interruption.
                  </div>
                  <button onClick={() => handleRenew()} style={{ padding: '8px 18px', background: '#f59e0b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    🔄 Renew Now
                  </button>
                </div>
              )}
              {daysLeft !== null && daysLeft <= 0 && (
                <div style={{ marginTop: 16, padding: '14px 16px', background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ fontSize: 13, color: '#ff4444' }}>
                    ❌ Your plan has expired. Renew now to restore access.
                  </div>
                  <button onClick={() => handleRenew()} style={{ padding: '8px 18px', background: '#ff4444', color: '#fff', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    🔄 Renew Now
                  </button>
                </div>
              )}
              {/* Always show renew button */}
              {daysLeft !== null && daysLeft > 14 && (
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => handleRenew()} style={{ padding: '9px 20px', background: 'rgba(0,213,91,0.1)', border: '1px solid #00d55b', color: '#00d55b', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                    🔄 Renew Plan
                  </button>
                </div>
              )}
            </div>

            {/* Payment History & Invoices */}
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 600 }}>Payment History & Invoices</h3>
                  <p style={{ fontSize: 12, color: '#555', marginTop: 2 }}>All your payment records and downloadable invoices</p>
                </div>
              </div>

              {payments.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#333', fontSize: 13 }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>💳</div>
                  No payment records yet
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#0d0d0d' }}>
                      {['Invoice', 'Date', 'Plan', 'Amount', 'TXN ID', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 11, color: '#555', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => {
                      const invoiceNo = `INV-${String(p.business_id).padStart(4,'0')}-${String(p.id).padStart(6,'0')}`;
                      const status = STATUS_COLORS[p.payment_status] || { bg: '#1a1a1a', color: '#555', label: p.payment_status };
                      const isApproved = p.payment_status === 'approved';
                      const pCurr = p.currency === 'usd' ? '$' : '₹';
                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid #0d0d0d' }}>
                          <td style={{ padding: '13px 16px', fontSize: 13, fontFamily: 'monospace', color: '#888' }}>{invoiceNo}</td>
                          <td style={{ padding: '13px 16px', fontSize: 12, color: '#888' }}>
                            {new Date(p.submitted_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                          </td>
                          <td style={{ padding: '13px 16px', fontSize: 13, color: '#fff' }}>
                            {p.plan_name || p.plan_key?.replace(/_/g,' ')}
                            <div style={{ fontSize: 11, color: '#555' }}>{p.billing_cycle === 'yearly' ? 'Yearly Plan' : p.billing_cycle === 'quarterly' ? 'Quarterly Plan' : 'Monthly Plan'}</div>
                          </td>
                          <td style={{ padding: '13px 16px', fontSize: 14, fontWeight: 700, color: '#00d55b' }}>
                            {pCurr}{Number(p.amount).toLocaleString('en-IN')}
                          </td>
                          <td style={{ padding: '13px 16px', fontSize: 12, fontFamily: 'monospace', color: '#888' }}>
                            {p.txn_id?.substring(0, 16)}{p.txn_id?.length > 16 ? '...' : ''}
                          </td>
                          <td style={{ padding: '13px 16px' }}>
                            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: status.bg, color: status.color, fontWeight: 600 }}>
                              {status.label}
                            </span>
                          </td>
                          <td style={{ padding: '13px 16px' }}>
                            {isApproved ? (
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={() => openInvoice(p.id)}
                                  style={{ padding: '5px 12px', background: 'rgba(0,213,91,0.1)', border: '1px solid #00d55b', color: '#00d55b', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                                  👁 View
                                </button>
                                <button onClick={() => printInvoice(p.id)}
                                  style={{ padding: '5px 12px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
                                  🖨 Print
                                </button>
                              </div>
                            ) : (
                              <span style={{ fontSize: 11, color: '#333' }}>Available after approval</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Contact for billing */}
            <div style={{ marginTop: 20, padding: '16px 20px', background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 12, fontSize: 13, color: '#555', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <span>Need to upgrade, renew or have billing questions?</span>
              <div style={{ display: 'flex', gap: 10 }}>
                <a href="mailto:halo@msdiscoveries.com" style={{ padding: '7px 16px', background: '#111', border: '1px solid #1e1e1e', color: '#888', borderRadius: 8, fontSize: 12, textDecoration: 'none' }}>
                  📧 halo@msdiscoveries.com
                </a>
                <a href="tel:+919443724724" style={{ padding: '7px 16px', background: '#00d55b', color: '#000', borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                  📞 Call Us
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </DashLayout>
  );
}